import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getPickems, type Pickem } from '@/api/pickems/getPickems';
import { useCreatePickemMutation } from '@/api/pickems/createPickem';
import { useUpdatePickemMutation } from '@/api/pickems/updatePickem';
import { useCompletePickemMutation } from '@/api/pickems/completePickem';
import { useMarkAsPaidOutMutation } from '@/api/pickems/markAsPaidOut';
import { useStartScoringMutation } from '@/api/pickems/startScoringAsync';
import { useRecalculatePickemRanksMutation } from '@/api/pickems/recalculatePickemRanks';
import { useDuplicatePickemMutation } from '@/api/pickems/duplicatePickem';
import { useRecalculatePrizesMutation } from '@/api/prize-payouts';
import { useScoringProgress } from '@/hooks/useScoringProgress';
import {
  useGetPickemLeaderboardQuery,
  downloadPickemLeaderboardCSV,
} from '@/api/leaderboard/getPickemLeaderboard';
import { useGetPickemEntriesQuery } from '@/api/pickems/getPickemEntries';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetTenantImageLibraryQuery } from '@/api/tenants/tenantImageLibrary';
import { useGetPayoutTemplatesQuery } from '@/api/tenants/payoutTemplates';
import { useGetQuestionsQuery, type Question } from '@/api/questions/getQuestions';
import { useReorderQuestionsMutation } from '@/api/questions/reorderQuestions';
import { useDeleteQuestionMutation } from '@/api/questions/deleteQuestion';
import { Title } from '@/components/composite/Title';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Download,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Copy,
  RefreshCw,
  DollarSign,
  Search,
  Info,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SortableQuestionsList } from '@/components/questions/SortableQuestionsList';
import { SelectionsDetail } from '@/components/pickems/SelectionsDetail';
import { PickemTimelineStepper } from '@/components/pickems/PickemTimelineStepper';
import { DuplicatePickemDialog } from '@/components/pickems/DuplicatePickemDialog';
import { PickemGoogleAd } from '@/components/pickems/PickemGoogleAd';
import { PickemStylingTab } from '@/components/pickems/PickemStylingTab';
import { type GoogleAdsPlacement } from '@/api/tenants/googleAdsPlacements';
import {
  useGetPickemGoogleAdQuery,
  usePutPickemGoogleAdMutation,
} from '@/api/pickems/pickemGoogleAd';
import { getSportLogoUrl } from '@low6-pickem/sports-config';
import {
  ENABLED_SPORTS,
  isContentLocaleEnabled,
  isFeedEnabled,
  sportsForSelect,
} from '@/lib/platform-filters';
import {
  defaultStyleConfig,
  normalizePickemHeaderConfigForForm,
  type GameType,
  type PickemHeaderStyleConfig,
} from '@low6-pickem/style-config';
import { AutoScoreModal } from '@/components/auto-scoring/AutoScoreModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { TzDateTimeInput } from '@/components/datetime/TzDateTimeInput';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { createPickemValidationSchema } from '@/validation/pickemSchema';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { RequestBody, queryKeys } from '@/api/react-query.config';
import { toast } from 'sonner';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { LiveScoringQuestionsList } from '@/components/live-scoring/LiveScoringQuestionsList';
import { ContentCard } from '@/components/layout/ContentCard';
import { PickemGameRulesSection } from '@/components/pickems/PickemGameRulesSection';
import { PickemMarketingConsentSection } from '@/components/pickems/PickemMarketingConsentSection';

// Use API-derived types for type safety
type CreatePickemRequest = RequestBody<'/api/admin/pickems', 'post'>;
type UpdatePickemRequest = RequestBody<'/api/admin/pickems/{id}', 'put'>;

// Form values use Create schema (more lenient with optional fields)
// We handle conversion to Update schema in handleSubmit
type PickemFormValues = CreatePickemRequest;

function getImageDisplayName(blobName: string) {
  const segments = blobName.split('/');
  return segments[segments.length - 1] || blobName;
}

function formatFileSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export const Route = createFileRoute('/dashboard/pickems/$id')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tenant_id: search.tenant_id as string | undefined,
      tab: search.tab as
        | 'basic'
        | 'questions'
        | 'entries'
        | 'leaderboard'
        | 'live-scoring'
        | 'styling'
        | undefined,
    };
  },
  // @ts-expect-error TanStack Router infers two structurally-identical-but-
  // not-equal Pickem types (style_config: { [key: string]: unknown } vs
  // { [x: string]: {} }), making the loader signature mismatch itself. The
  // body is correct at runtime; this comment records that the type error is
  // inherited from main's PR #476 introduction of style_config — needs a
  // dedicated fix (openapi-typescript / TypeBox compatibility) outside this PR.
  loader: async ({ params }) => {
    const { pickems } = await getPickems();
    const pickem = pickems.find(p => p.pickem_id === params.id);
    return { pickem };
  },
  component: RouteComponent,
});

export function RouteComponent() {
  const { pickem } = Route.useLoaderData() as { pickem: Pickem | undefined };
  const { id: pickemRouteId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesSearchInput, setEntriesSearchInput] = useState('');
  const [entriesDebouncedSearch, setEntriesDebouncedSearch] = useState('');
  const entriesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [questionOrder, setQuestionOrder] = useState<string[] | null>(null);
  const [scoringPickemId, setScoringPickemId] = useState<string | null>(pickem?.pickem_id || null);
  const [autoScoreModalOpen, setAutoScoreModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [currentTenantId, setCurrentTenantId] = useState<string | undefined>(
    pickem?.tenant_id || search.tenant_id
  );
  const [bannerLibraryOpen, setBannerLibraryOpen] = useState(false);
  const [bannerLibrarySearch, setBannerLibrarySearch] = useState('');
  const [gameRulesVersionId, setGameRulesVersionId] = useState<string | undefined>(
    (pickem as any)?.game_rules_version_id || undefined
  );
  const [marketingConsentVersionId, setMarketingConsentVersionId] = useState<string | undefined>(
    (pickem as any)?.marketing_consent_version_id || undefined
  );

  useEffect(() => {
    setMarketingConsentVersionId((pickem as any)?.marketing_consent_version_id || undefined);
  }, [pickem?.pickem_id, (pickem as any)?.marketing_consent_version_id]);

  // Google Ad placement - fetch from API (contest card)
  const { data: googleAdFromApi } = useGetPickemGoogleAdQuery(pickem?.pickem_id);
  const { mutate: saveGoogleAd, isPending: isSavingGoogleAd } = usePutPickemGoogleAdMutation(
    pickem?.pickem_id
  );

  // Google Ad placement - fetch from API (in-play sticky footer, DKPLAT-468)
  const { data: stickyFooterAdFromApi } = useGetPickemGoogleAdQuery(
    pickem?.pickem_id,
    'PICKEM_STICKY_FOOTER'
  );
  const { mutate: saveStickyFooterAd, isPending: isSavingStickyFooterAd } =
    usePutPickemGoogleAdMutation(pickem?.pickem_id, 'PICKEM_STICKY_FOOTER');

  // Google Ad placement state
  const [googleAd, setGoogleAd] = useState<GoogleAdsPlacement | null>(null);
  const [stickyFooterAd, setStickyFooterAd] = useState<GoogleAdsPlacement | null>(null);

  // Sync googleAd state when API data changes
  useEffect(() => {
    if (googleAdFromApi) {
      setGoogleAd({
        ...googleAdFromApi,
        placementKey: 'PICKEM_CONTEST_CARD',
      });
    } else {
      setGoogleAd(null);
    }
  }, [googleAdFromApi]);

  // Sync sticky footer state when API data changes
  useEffect(() => {
    if (stickyFooterAdFromApi) {
      setStickyFooterAd({
        ...stickyFooterAdFromApi,
        placementKey: 'PICKEM_STICKY_FOOTER',
      });
    } else {
      setStickyFooterAd(null);
    }
  }, [stickyFooterAdFromApi]);

  // Debounce entries search — reset to page 1 when search changes
  useEffect(() => {
    if (entriesDebounceRef.current) clearTimeout(entriesDebounceRef.current);
    entriesDebounceRef.current = setTimeout(() => {
      setEntriesDebouncedSearch(entriesSearchInput);
      setEntriesPage(1);
    }, 300);
    return () => {
      if (entriesDebounceRef.current) clearTimeout(entriesDebounceRef.current);
    };
  }, [entriesSearchInput]);

  const { data: tenants = [], isLoading: tenantsLoading } = useGetTenantsQuery();
  const currentTenantCode = useMemo(
    () => tenants.find(tenant => tenant.tenant_id === currentTenantId)?.code,
    [tenants, currentTenantId]
  );
  const {
    data: tenantImageLibrary,
    isLoading: isTenantImageLibraryLoading,
    isFetching: isTenantImageLibraryFetching,
    isError: isTenantImageLibraryError,
    refetch: refetchTenantImageLibrary,
  } = useGetTenantImageLibraryQuery(currentTenantCode);
  const filteredTenantImages = useMemo(() => {
    const images = tenantImageLibrary?.images ?? [];
    const query = bannerLibrarySearch.trim().toLowerCase();
    if (!query) return images;
    return images.filter(image => {
      const name = getImageDisplayName(image.name).toLowerCase();
      return name.includes(query) || image.name.toLowerCase().includes(query);
    });
  }, [tenantImageLibrary?.images, bannerLibrarySearch]);
  const { data: allQuestions = [], isLoading: questionsLoading } = useGetQuestionsQuery();

  // Track tenant for payout templates query
  const { data: payoutTemplatesData } = useGetPayoutTemplatesQuery(currentTenantId);
  const pickemPayoutTemplates = useMemo(() => {
    return (payoutTemplatesData?.templates || []).filter(t => t.gameType === 'pickem');
  }, [payoutTemplatesData]);

  // Call all hooks before any conditional returns
  const { mutate: createMutate, isPending: createIsPending } = useCreatePickemMutation();
  const { mutate: updateMutate, isPending: updateIsPending } = useUpdatePickemMutation();
  const { mutateAsync: updateStylingMutateAsync, isPending: updateStylingIsPending } =
    useUpdatePickemMutation({ redirect: false });
  const { mutate: completeMutate, isPending: completeIsPending } = useCompletePickemMutation();
  const { mutate: startScoringMutate, isPending: startScoringIsPending } =
    useStartScoringMutation();
  const { mutate: recalculateRanksMutate, isPending: recalculateRanksIsPending } =
    useRecalculatePickemRanksMutation();
  const { mutate: duplicateMutate, isPending: duplicateIsPending } = useDuplicatePickemMutation();
  const { mutate: markAsPaidOutMutate, isPending: markAsPaidOutIsPending } =
    useMarkAsPaidOutMutation();
  const { mutate: recalculatePrizesMutate, isPending: recalculatePrizesIsPending } =
    useRecalculatePrizesMutation();
  const { mutate: reorderQuestions } = useReorderQuestionsMutation();
  const { mutateAsync: deleteQuestionMutate, isPending: deleteQuestionIsPending } =
    useDeleteQuestionMutation();

  // SSE scoring progress hook - always enabled to detect in-progress scoring
  useScoringProgress({
    pickemId: scoringPickemId,
    enabled: !!scoringPickemId,
    onComplete: () => {
      // Invalidate queries when scoring completes - React Query will refetch automatically
      queryClient.invalidateQueries({ queryKey: queryKeys.pickems.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.questions.all });
      if (scoringPickemId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.pickem(scoringPickemId) });
      }

      setTimeout(() => {
        setScoringPickemId(null);
      }, 2500);
    },
    onError: error => {
      toast.error(`Scoring failed: ${error}`);
    },
  });

  // Always call the hook, but conditionally enable it
  const isPickemComplete = pickem?.status === 'COMPLETE';
  const pickemStatus = pickem?.status || (pickem as any)?.pickem_status;
  const isPickemInPlay = pickemStatus === 'INPLAY' || pickemStatus === 'ACTIVE';
  const isPickemScored = !!(pickem as any)?.scored;

  // Check editable flag from API response
  const isPickemEditable = pickem?.editable !== false; // Default to true if not present
  const pickemEditMessage = pickem?.edit_message;

  const { data: leaderboardData, isLoading: leaderboardLoading } = useGetPickemLeaderboardQuery(
    isPickemComplete ? pickem?.pickem_id : undefined
  );

  // Fetch pickem entries
  const entriesQueryParams = {
    page: entriesPage,
    limit: 50,
    search: entriesDebouncedSearch || undefined,
  };
  const { data: entriesData, isLoading: entriesLoading } = useGetPickemEntriesQuery(
    pickem?.pickem_id,
    entriesQueryParams
  );

  // Translation hook
  const { translateField, isFieldTranslating } = useFieldTranslation();

  // Whether this instance authors Contest text in a second Content Locale (CAE-762).
  // Gates the render only — the fields stay in form state, because the founding client
  // still authors them and this component serves both.
  const hasSecondaryContentLocale = isContentLocaleEnabled('fr');

  // Memoised so its reference stays stable across re-renders that don't change the
  // underlying questions. Previously this filter ran on every render, producing a
  // new array each time; a drag-reorder calls setQuestionOrder (a re-render), and
  // SortableQuestionsList's `initialQuestions` effect then reset its local order
  // back to this (still server-ordered) array — so reordering never appeared to
  // take. A stable reference lets the effect fire only on real question changes
  // (add/remove/edit/save-refetch), not on unrelated re-renders (DKPLAT-514).
  const pickemQuestions = useMemo(
    () => allQuestions.filter(q => q.pickem_id === pickem?.pickem_id),
    [allQuestions, pickem?.pickem_id]
  );

  if (tenantsLoading || questionsLoading) {
    return (
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>Loading...</Title>
            <BackButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-muted-foreground">Loading pickem data...</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if all questions have a correct answer marked
  const allQuestionsHaveCorrectAnswer = pickemQuestions.every(q =>
    q.question_type === 'tiebreaker'
      ? q.correct_value !== undefined && q.correct_value !== null
      : q.options?.some(opt => opt.is_correct === true)
  );

  const questionsWithoutCorrectAnswer = pickemQuestions.filter(q =>
    q.question_type === 'tiebreaker'
      ? !(q.correct_value !== undefined && q.correct_value !== null)
      : !q.options?.some(opt => opt.is_correct === true)
  ).length;

  const handleCompletePickem = () => {
    if (!pickem?.pickem_id) return;

    // Check pickem status before attempting to score
    const currentStatus = pickem.status || (pickem as any).pickem_status;
    if (currentStatus !== 'INPLAY' && currentStatus !== 'ACTIVE') {
      toast.error(`Cannot score pickem: Status must be INPLAY (current: ${currentStatus})`);
      return;
    }

    // Score the pickem (status stays INPLAY — payout is separate step)
    completeMutate(
      { pickemId: pickem.pickem_id },
      {
        onSuccess: () => {
          // Start SSE-based scoring progress tracking
          startScoringMutate(pickem.pickem_id, {
            onSuccess: () => {
              setScoringPickemId(pickem.pickem_id);
            },
          });
        },
      }
    );
  };

  const handleMarkAsPaidOut = () => {
    if (!pickem?.pickem_id) return;
    markAsPaidOutMutate({ pickemId: pickem.pickem_id });
  };

  const handleDownloadCSV = async () => {
    if (pickem?.pickem_id) {
      try {
        await downloadPickemLeaderboardCSV(pickem.pickem_id);
      } catch (error) {
        console.error('Failed to download CSV:', error);
      }
    }
  };

  const handleForceScoring = async () => {
    if (pickem?.pickem_id) {
      // Use SSE-based scoring
      startScoringMutate(pickem.pickem_id, {
        onSuccess: () => {
          setScoringPickemId(pickem.pickem_id);
        },
      });
    }
  };

  const handleRecalculateRanks = async () => {
    if (pickem?.pickem_id) {
      recalculateRanksMutate(pickem.pickem_id);
    }
  };

  const handleCalculatePrizes = async () => {
    if (pickem?.pickem_id) {
      recalculatePrizesMutate(pickem.pickem_id);
    }
  };

  const handleDuplicatePickem = (leagueId: string) => {
    if (pickem?.pickem_id) {
      duplicateMutate({ pickemId: pickem.pickem_id, tenant_id: leagueId });
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      await deleteQuestionMutate(questionToDelete.question_id);
      setQuestionToDelete(null);
    } catch {
      // Error toast is handled by the mutation
    }
  };

  const existingStyleConfig =
    (pickem as { style_config?: Record<string, unknown> } | undefined)?.style_config ??
    defaultStyleConfig('pickem');

  const initialHeaderConfig = normalizePickemHeaderConfigForForm(
    existingStyleConfig.header as PickemHeaderStyleConfig | undefined
  );

  const initialValues: PickemFormValues = {
    tenant_id: pickem?.tenant_id || search.tenant_id || '',
    title_en: pickem?.title_en || '',
    title_fr: pickem?.title_fr,
    start: pickem?.start || undefined,
    allow_selection_start: pickem?.allow_selection_start || undefined,
    questions_posted_at: pickem?.questions_posted_at || undefined,
    reserve_a_seat_enabled: (pickem as any)?.reserve_a_seat_enabled ?? false,
    pickem_status: pickem?.status,
    // Falls back to this client's FIRST declared sport rather than a hardcoded 'NFL',
    // which was one client's league standing in as every client's default.
    sport:
      (pickem?.sport as PickemFormValues['sport'] | undefined) ||
      ((ENABLED_SPORTS[0]?.code ?? 'NFL') as PickemFormValues['sport']),
    prize_text_en: pickem?.prize_text_en,
    prize_text_fr: pickem?.prize_text_fr,
    payout_template_id: pickem?.payout_template_id || undefined,
    league_ip: pickem?.league_ip ?? false,
    lobby_hidden: pickem?.lobby_hidden ?? false,
    style_config: {
      ...existingStyleConfig,
      header: initialHeaderConfig,
      showPopularPicks: existingStyleConfig.showPopularPicks === true,
      showTermsCheckbox: existingStyleConfig.showTermsCheckbox === true,
      showMarketingConsentModal: existingStyleConfig.showMarketingConsentModal === true,
      lowResImages: existingStyleConfig.lowResImages === true,
    },
  };

  const handleSubmit = async (values: PickemFormValues, { setSubmitting }: any) => {
    try {
      if (pickem) {
        // Convert to UpdatePickemRequest (requires non-optional fields)
        const updatePayload: UpdatePickemRequest & { id: string } = {
          id: pickem.pickem_id,
          title_en: values.title_en,
          title_fr: values.title_fr,
          tenant_id: values.tenant_id,
          start: values.start || new Date().toISOString(),
          allow_selection_start: values.allow_selection_start,
          questions_posted_at: values.questions_posted_at || null,
          reserve_a_seat_enabled: values.reserve_a_seat_enabled ?? false,
          pickem_status: values.pickem_status,
          sport: values.sport,
          prize_text_en: values.prize_text_en || '',
          prize_text_fr: values.prize_text_fr,
          gamezone_image_en: values.gamezone_image_en,
          gamezone_image_fr: values.gamezone_image_fr,
          text_gamezone_image_en: values.text_gamezone_image_en,
          text_gamezone_image_fr: values.text_gamezone_image_fr,
          text_main_image_en: values.text_main_image_en,
          text_main_image_fr: values.text_main_image_fr,
          prizes_desc_en: values.prizes_desc_en,
          prizes_desc_fr: values.prizes_desc_fr,
          pin_user_ids: values.pin_user_ids,
          payout_template_id: values.payout_template_id || null,
          league_ip: values.league_ip,
          lobby_hidden: values.lobby_hidden,
          game_rules_version_id: gameRulesVersionId || undefined,
          marketing_consent_version_id: marketingConsentVersionId || undefined,
          style_config: values.style_config,
        } as any;

        await new Promise((resolve, reject) => {
          updateMutate(updatePayload as any, {
            onSuccess: resolve,
            onError: reject,
          });
        });

        // Save Google Ad placement separately if it exists
        if (googleAd && pickem.pickem_id) {
          await new Promise((resolve, reject) => {
            saveGoogleAd(googleAd, {
              onSuccess: resolve,
              onError: reject,
            });
          });
        }

        // Save the in-play sticky footer ad placement if it exists (DKPLAT-468)
        if (stickyFooterAd && pickem.pickem_id) {
          await new Promise((resolve, reject) => {
            saveStickyFooterAd(stickyFooterAd, {
              onSuccess: resolve,
              onError: reject,
            });
          });
        }

        // If question order has changed, save it
        if (questionOrder) {
          await new Promise<void>((resolve, reject) => {
            reorderQuestions(
              { pickemId: pickem.pickem_id, questionIds: questionOrder },
              {
                onSuccess: () => {
                  setQuestionOrder(null); // Reset after successful save
                  resolve();
                },
                onError: reject,
              }
            );
          });
        }
      } else {
        // Send only the display flags (showPopularPicks/showTermsCheckbox) on create —
        // NOT the full client style_config — so the backend still applies the tenant
        // default style and merges these flags over it. (DKPLAT-391)
        const { style_config: fullStyleConfig, ...rest } = values;
        const sc = (fullStyleConfig ?? {}) as Record<string, unknown>;
        const createPayload = {
          ...rest,
          game_rules_version_id: gameRulesVersionId || undefined,
          marketing_consent_version_id: marketingConsentVersionId || undefined,
          style_config: {
            showPopularPicks: sc.showPopularPicks === true,
            showTermsCheckbox: sc.showTermsCheckbox === true,
            showMarketingConsentModal: sc.showMarketingConsentModal === true,
            lowResImages: sc.lowResImages === true,
          },
        };
        await new Promise((resolve, reject) => {
          createMutate(createPayload as CreatePickemRequest, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      }
    } catch (error) {
      console.error('Failed to save pickem:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ pickems: "Pick'em" }}
        entityName={pickem?.title_en || (pickemRouteId === 'new' ? 'New Contest' : 'Edit')}
      />
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>{pickem ? 'Edit' : 'Add new'} pickem</Title>
            <div className="flex items-center gap-2">
              {pickem && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDuplicateDialogOpen(true)}
                  disabled={duplicateIsPending}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </Button>
              )}
              <BackButton />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Timeline Stepper - only show for existing pickems */}
          {pickem && (
            <PickemTimelineStepper
              pickem={pickem}
              allQuestionsHaveCorrectAnswer={allQuestionsHaveCorrectAnswer}
              questionsCount={pickemQuestions.length}
            />
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={createPickemValidationSchema(pickemQuestions)}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={true}
          >
            {({
              isSubmitting,
              errors,
              touched,
              values,
              setFieldValue,
              setFieldTouched,
            }: FormikProps<PickemFormValues>) => {
              const durationWarning = useMemo(() => {
                if (values.start && values.allow_selection_start) {
                  const startDate = new Date(values.start);
                  const openDate = new Date(values.allow_selection_start);
                  const diffMs = startDate.getTime() - openDate.getTime();
                  const diffHours = diffMs / (1000 * 60 * 60);

                  if (diffHours > 0 && diffHours < 24) {
                    const hours = Math.floor(diffHours);
                    const minutes = Math.floor((diffHours - hours) * 60);
                    return { hours, minutes };
                  }
                }
                return null;
              }, [values.start, values.allow_selection_start]);

              const activeTab = search.tab || 'basic';

              return (
                <Form className="flex flex-col gap-6">
                  <Tabs
                    value={activeTab}
                    onValueChange={value =>
                      navigate({
                        to: '/dashboard/pickems/$id',
                        params: { id: pickemRouteId },
                        search: {
                          tenant_id: search.tenant_id,
                          tab: value as
                            | 'basic'
                            | 'questions'
                            | 'entries'
                            | 'leaderboard'
                            | 'live-scoring'
                            | 'styling',
                        },
                      })
                    }
                    className="w-full"
                  >
                    <TabsList
                      className={`grid w-full ${isPickemComplete ? 'grid-cols-7' : pickem ? 'grid-cols-6' : 'grid-cols-1'}`}
                    >
                      <TabsTrigger value="basic">Basic Info</TabsTrigger>
                      {pickem && <TabsTrigger value="google-ad">Google Ad</TabsTrigger>}
                      {pickem && <TabsTrigger value="questions">Questions</TabsTrigger>}
                      {pickem && <TabsTrigger value="live-scoring">Live Scoring</TabsTrigger>}
                      {pickem && <TabsTrigger value="entries">Entries</TabsTrigger>}
                      {pickem && <TabsTrigger value="styling">Styling</TabsTrigger>}
                      {isPickemComplete && (
                        <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
                      )}
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="tenant_id">Tenant *</Label>
                        <Select
                          value={values.tenant_id || ''}
                          onValueChange={value => {
                            setFieldValue('tenant_id', value);
                            setFieldValue('payout_template_id', undefined);
                            setCurrentTenantId(value);
                          }}
                          disabled={tenantsLoading || !isPickemEditable}
                        >
                          <SelectTrigger
                            data-testid="pickem-tenant_id-select"
                            className={
                              errors.tenant_id && touched.tenant_id ? 'border-red-500' : ''
                            }
                          >
                            <SelectValue placeholder="Select a tenant" />
                          </SelectTrigger>
                          <SelectContent>
                            {tenants.map(tenant => (
                              <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                                {tenant.name_en || tenant.title || `Tenant #${tenant.tenant_id}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <ErrorMessage name="tenant_id">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Sport Selection. Kept on a feed-less client too: it identifies what a
                          Contest is ABOUT, which is useful whether or not a feed backs it. The
                          codes such a client declares are sport codes ('CRICKET'), whose artwork
                          is generic — so this offers no trademark, in its labels or its icons. */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="sport">Sport</Label>
                        <Select
                          value={(values.sport as string) || ENABLED_SPORTS[0]?.code || 'NFL'}
                          onValueChange={value => {
                            setFieldValue('sport', value);
                            setFieldTouched('sport', true);
                          }}
                          disabled={!isPickemEditable}
                        >
                          <SelectTrigger
                            data-testid="pickem-sport-select"
                            className={
                              errors.sport && touched.sport
                                ? 'border-red-500'
                                : !isPickemEditable
                                  ? 'bg-gray-50 cursor-not-allowed'
                                  : ''
                            }
                          >
                            <SelectValue placeholder="Select sport" />
                          </SelectTrigger>
                          <SelectContent>
                            {sportsForSelect(values.sport as string).map(league => (
                              <SelectItem key={league.code} value={league.code}>
                                <div className="flex items-center gap-2">
                                  <img
                                    src={getSportLogoUrl(league.code)}
                                    alt={league.code}
                                    className="w-6 h-6 object-contain"
                                  />
                                  <span>{league.filterLabel || league.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                            {(ENABLED_SPORTS.length > 1 || values.sport === 'Mixed') && (
                              <SelectItem value="Mixed">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={getSportLogoUrl('MIXED')}
                                    alt="Mixed"
                                    className="w-6 h-6 object-contain"
                                  />
                                  <span>Mixed</span>
                                </div>
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <ErrorMessage name="sport">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Payout Template Selection */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="payout_template_id">Payout Template</Label>
                        <Select
                          value={values.payout_template_id || 'none'}
                          onValueChange={value => {
                            setFieldValue(
                              'payout_template_id',
                              value === 'none' ? undefined : value
                            );
                          }}
                          disabled={!values.tenant_id || !isPickemEditable}
                        >
                          <SelectTrigger
                            data-testid="pickem-payout_template_id-select"
                            className={!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}
                          >
                            <SelectValue
                              placeholder={
                                !values.tenant_id
                                  ? 'Select a tenant first'
                                  : 'None (no payout template)'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {pickemPayoutTemplates.map(template => (
                              <SelectItem key={template.id} value={template.id}>
                                {template.title} ({template.format})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Optional. Select a payout template to define prize distribution.
                        </p>
                      </div>

                      <div
                        className={
                          hasSecondaryContentLocale
                            ? 'grid grid-cols-2 gap-4'
                            : 'flex flex-col gap-2'
                        }
                      >
                        <div className="flex flex-col gap-2">
                          {hasSecondaryContentLocale ? (
                            <TranslatableFieldLabel
                              htmlFor="title_en"
                              onTranslate={() =>
                                translateField(
                                  'title_en',
                                  values.title_en,
                                  'title_fr',
                                  setFieldValue
                                )
                              }
                              isTranslating={isFieldTranslating('title_en')}
                            >
                              Title (English)
                            </TranslatableFieldLabel>
                          ) : (
                            <Label htmlFor="title_en">Title</Label>
                          )}
                          <Field
                            as={Input}
                            id="title_en"
                            name="title_en"
                            data-testid="pickem-title_en"
                            placeholder={
                              hasSecondaryContentLocale
                                ? 'Enter pickem title in English'
                                : 'Enter pickem title'
                            }
                            readOnly={!isPickemEditable}
                            disabled={!isPickemEditable}
                            className={`${errors.title_en && touched.title_en ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <ErrorMessage name="title_en">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        {hasSecondaryContentLocale && (
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="title_fr">Title (Spanish)</Label>
                            <Field
                              as={Input}
                              id="title_fr"
                              name="title_fr"
                              data-testid="pickem-title_fr"
                              placeholder="Enter pickem title in Spanish"
                              readOnly={isFieldTranslating('title_en') || !isPickemEditable}
                              disabled={!isPickemEditable}
                              className={`${errors.title_fr && touched.title_fr ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            />
                            <ErrorMessage name="title_fr">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>
                        )}
                      </div>
                      <div
                        className={
                          hasSecondaryContentLocale
                            ? 'grid grid-cols-2 gap-4'
                            : 'flex flex-col gap-2'
                        }
                      >
                        <div className="flex flex-col gap-2">
                          {hasSecondaryContentLocale ? (
                            <TranslatableFieldLabel
                              htmlFor="prize_text_en"
                              onTranslate={() =>
                                translateField(
                                  'prize_text_en',
                                  values.prize_text_en || '',
                                  'prize_text_fr',
                                  setFieldValue
                                )
                              }
                              isTranslating={isFieldTranslating('prize_text_en')}
                            >
                              Prize Text (English)
                            </TranslatableFieldLabel>
                          ) : (
                            <Label htmlFor="prize_text_en">Prize Text</Label>
                          )}
                          <Field
                            as={Input}
                            id="prize_text_en"
                            name="prize_text_en"
                            data-testid="pickem-prize_text_en"
                            placeholder="Enter prize description"
                            readOnly={!isPickemEditable}
                            disabled={!isPickemEditable}
                            className={`${errors.prize_text_en && touched.prize_text_en ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <ErrorMessage name="prize_text_en">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        {hasSecondaryContentLocale && (
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="prize_text_fr">Prize Text (Spanish)</Label>
                            <Field
                              as={Input}
                              id="prize_text_fr"
                              name="prize_text_fr"
                              data-testid="pickem-prize_text_fr"
                              placeholder="Enter prize description"
                              readOnly={isFieldTranslating('prize_text_en') || !isPickemEditable}
                              disabled={!isPickemEditable}
                              className={`${errors.prize_text_fr && touched.prize_text_fr ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            />
                            <ErrorMessage name="prize_text_fr">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <TzDateTimeInput
                            label="Open Date & Time"
                            id="allow_selection_start"
                            data-testid="pickem-allow_selection_start"
                            value={values.allow_selection_start}
                            onChange={value => setFieldValue('allow_selection_start', value)}
                            disabled={!isPickemEditable}
                            required
                            className={`${errors.allow_selection_start && touched.allow_selection_start ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            This is the date/time from when this pickem is visible to users.
                          </p>
                          <ErrorMessage name="allow_selection_start">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        <div className="flex flex-col gap-2">
                          <TzDateTimeInput
                            label="Start Date & Time"
                            id="start"
                            data-testid="pickem-start"
                            value={values.start}
                            onChange={value => setFieldValue('start', value)}
                            disabled={!isPickemEditable}
                            required
                            className={`${errors.start && touched.start ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            After this date/time users will no longer be able to make selections.
                          </p>
                          <ErrorMessage name="start">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <TzDateTimeInput
                            label="Questions Posted At"
                            id="questions_posted_at"
                            data-testid="pickem-questions_posted_at"
                            value={values.questions_posted_at || ''}
                            onChange={value =>
                              setFieldValue('questions_posted_at', value || undefined)
                            }
                            disabled={!isPickemEditable}
                            description="When questions will be posted for this contest — shown to users on the Reserve Entry screen."
                            className={`${errors.questions_posted_at && touched.questions_posted_at ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <ErrorMessage name="questions_posted_at">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="reserve_a_seat_enabled"
                            data-testid="pickem-reserve_a_seat_enabled"
                            checked={values.reserve_a_seat_enabled ?? false}
                            onCheckedChange={checked =>
                              setFieldValue('reserve_a_seat_enabled', checked)
                            }
                            disabled={!isPickemEditable}
                          />
                          <Label htmlFor="reserve_a_seat_enabled" className="cursor-pointer">
                            Reserve a Seat
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          When on, users can reserve a seat before questions post. The reserve flow
                          only triggers when this toggle is ON <strong>and</strong> a “Questions
                          Posted At” time is set (and the contest is not yet closed). Turning this
                          off falls back to the normal picks flow.
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id="league_ip"
                          checked={values.league_ip ?? false}
                          onCheckedChange={checked => setFieldValue('league_ip', checked)}
                          disabled={!isPickemEditable}
                        />
                        <div className="flex items-center gap-1">
                          <Label htmlFor="league_ip" className="cursor-pointer">
                            League IP
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, newly-picked options use licensed imagery (team logos &
                                player headshots); when off, generic imagery (team avatars & player
                                jerseys). Off by default. Changing this only affects options you
                                pick from now on — re-pick existing options to refresh their images.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id="lobby_hidden"
                          checked={values.lobby_hidden ?? false}
                          onCheckedChange={checked => setFieldValue('lobby_hidden', checked)}
                          disabled={!isPickemEditable}
                        />
                        <div className="flex items-center gap-1">
                          <Label htmlFor="lobby_hidden" className="cursor-pointer">
                            Hide from Lobby
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, this contest won't appear in the Lobby (sections, search or
                                featured rows) — players can only enter via its direct URL. Players
                                who have joined still see it in My Games. Off by default.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      {/* Display options */}
                      <div className="flex flex-col gap-3 rounded-md border p-4">
                        <p className="text-sm font-medium">Display options</p>

                        <div className="flex items-center gap-2">
                          <Switch
                            id="style_config.showPopularPicks"
                            checked={
                              (values.style_config as Record<string, unknown>)?.showPopularPicks ===
                              true
                            }
                            onCheckedChange={checked =>
                              setFieldValue('style_config', {
                                ...(values.style_config as Record<string, unknown>),
                                showPopularPicks: checked,
                              })
                            }
                            disabled={!isPickemEditable}
                          />
                          <div className="flex items-center gap-1">
                            <Label
                              htmlFor="style_config.showPopularPicks"
                              className="cursor-pointer"
                            >
                              Show popular picks &amp; pick percentages
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm">
                                  When off, the Popular Pick button and the pick-percentage badges
                                  are hidden on the player frontend. Percentages are still tracked.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            id="style_config.showTermsCheckbox"
                            checked={
                              (values.style_config as Record<string, unknown>)
                                ?.showTermsCheckbox === true
                            }
                            onCheckedChange={checked =>
                              setFieldValue('style_config', {
                                ...(values.style_config as Record<string, unknown>),
                                showTermsCheckbox: checked,
                              })
                            }
                            disabled={!isPickemEditable}
                          />
                          <div className="flex items-center gap-1">
                            <Label
                              htmlFor="style_config.showTermsCheckbox"
                              className="cursor-pointer"
                            >
                              Show terms &amp; conditions checkbox on entry
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm">
                                  When off, players are not shown the T&amp;Cs agreement checkbox
                                  before submitting an entry.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            id="style_config.showMarketingConsentModal"
                            checked={
                              (values.style_config as Record<string, unknown>)
                                ?.showMarketingConsentModal === true
                            }
                            onCheckedChange={checked =>
                              setFieldValue('style_config', {
                                ...(values.style_config as Record<string, unknown>),
                                showMarketingConsentModal: checked,
                              })
                            }
                            disabled={!isPickemEditable}
                          />
                          <div className="flex items-center gap-1">
                            <Label
                              htmlFor="style_config.showMarketingConsentModal"
                              className="cursor-pointer"
                            >
                              Show brand marketing consent on entry
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm">
                                  When on, players see a landing consent modal with sponsor copy
                                  before making picks. Requires checkbox opt-in before continuing.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>

                        {(values.style_config as Record<string, unknown>)
                          ?.showMarketingConsentModal === true && (
                          <PickemMarketingConsentSection
                            tenantId={values.tenant_id || currentTenantId}
                            disabled={!isPickemEditable}
                            marketingConsentVersionId={marketingConsentVersionId}
                            onMarketingConsentVersionChange={setMarketingConsentVersionId}
                          />
                        )}
                      </div>

                      {/* Status field - only show when editing (readonly) */}
                      {pickem && (
                        <div className="flex flex-col gap-2">
                          <Label htmlFor="pickem_status">Status</Label>
                          <Input
                            id="pickem_status"
                            value={(values.pickem_status || 'ACTIVE') as string}
                            readOnly
                            disabled
                            className="bg-gray-50 cursor-not-allowed"
                            data-testid="pickem-pickem_status-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Status is automatically managed: DRAFT → SCHEDULED → OPEN → INPLAY →
                            COMPLETE. Use "Score Pickem" in the Questions tab to score, then "Mark
                            as Paid Out" to complete.
                          </p>
                        </div>
                      )}

                      {pickem && (
                        <PickemGameRulesSection
                          disabled={isPickemComplete}
                          gameRulesVersionId={gameRulesVersionId}
                          onGameRulesVersionChange={setGameRulesVersionId}
                        />
                      )}
                    </TabsContent>

                    {pickem && (
                      <TabsContent value="google-ad" className="space-y-8">
                        <PickemGoogleAd
                          pickem={pickem}
                          placement={googleAd}
                          onPlacementChange={setGoogleAd}
                          isSaving={isSubmitting || updateIsPending || isSavingGoogleAd}
                        />
                        <PickemGoogleAd
                          pickem={pickem}
                          placement={stickyFooterAd}
                          onPlacementChange={setStickyFooterAd}
                          isSaving={isSubmitting || updateIsPending || isSavingStickyFooterAd}
                          placementKey="PICKEM_STICKY_FOOTER"
                          title="In-Play Sticky Footer Ad Placement"
                          description="Configure the floating bottom ad banner shown while players make their picks (320x50 mobile / 728x90 desktop). Changes will be saved when you save the pickem."
                          formLabel="Sticky Footer Placement"
                          formDescription="Floating bottom banner shown during the in-play picks experience"
                          defaultSizes={[
                            [320, 50],
                            [728, 90],
                          ]}
                        />
                      </TabsContent>
                    )}

                    {pickem && (
                      <TabsContent value="questions" className="space-y-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-lg font-semibold">Questions</h3>
                            <p className="text-sm text-gray-600">
                              Drag and drop to reorder questions
                            </p>
                          </div>
                          {isPickemEditable && (
                            <Button
                              onClick={() =>
                                navigate({
                                  to: '/dashboard/questions/$id',
                                  params: { id: 'new' },
                                  search: { pickem_id: pickem.pickem_id },
                                })
                              }
                              variant="outline"
                              size="sm"
                              type="button"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Add Question
                            </Button>
                          )}
                        </div>

                        {questionsWithoutCorrectAnswer > 0 && (
                          <Alert className="bg-yellow-50 border-yellow-200">
                            <AlertDescription className="text-yellow-800">
                              {questionsWithoutCorrectAnswer}{' '}
                              {questionsWithoutCorrectAnswer === 1
                                ? 'question needs'
                                : 'questions need'}{' '}
                              a correct answer marked before the pickem can be completed.
                            </AlertDescription>
                          </Alert>
                        )}

                        <SortableQuestionsList
                          questions={pickemQuestions}
                          onOrderChange={setQuestionOrder}
                          onDelete={question => setQuestionToDelete(question)}
                          isReorderable={isPickemEditable}
                        />

                        <div className="pt-4 border-t flex flex-col gap-2">
                          <Button
                            onClick={handleCompletePickem}
                            disabled={
                              !allQuestionsHaveCorrectAnswer ||
                              !isPickemInPlay ||
                              isPickemComplete ||
                              completeIsPending ||
                              startScoringIsPending
                            }
                            variant={
                              allQuestionsHaveCorrectAnswer && isPickemInPlay && !isPickemComplete
                                ? 'default'
                                : 'outline'
                            }
                            className="w-full"
                            type="button"
                          >
                            {completeIsPending || startScoringIsPending
                              ? 'Processing...'
                              : isPickemComplete
                                ? 'Pickem Already Completed'
                                : !isPickemInPlay
                                  ? `Cannot Score - Pickem Status: ${pickemStatus || 'Unknown'} (Must be INPLAY)`
                                  : !allQuestionsHaveCorrectAnswer
                                    ? `Cannot Score - ${questionsWithoutCorrectAnswer} Question${questionsWithoutCorrectAnswer === 1 ? '' : 's'} Missing Correct Answer`
                                    : 'Score Pickem'}
                          </Button>
                          {!isPickemComplete && isPickemInPlay && allQuestionsHaveCorrectAnswer && (
                            <p className="text-xs text-center text-gray-500 mt-2">
                              This will calculate scores and update the leaderboard. The pickem
                              status will remain INPLAY until you mark it as paid out.
                            </p>
                          )}

                          {isPickemInPlay && isPickemScored && !isPickemComplete && (
                            <Button
                              onClick={handleMarkAsPaidOut}
                              disabled={markAsPaidOutIsPending}
                              variant="default"
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                              type="button"
                            >
                              {markAsPaidOutIsPending ? 'Processing...' : 'Mark as Paid Out'}
                            </Button>
                          )}
                          {isPickemInPlay && isPickemScored && !isPickemComplete && (
                            <p className="text-xs text-center text-gray-500">
                              Pickem has been scored. Click "Mark as Paid Out" to move to COMPLETE
                              status after payout is confirmed.
                            </p>
                          )}
                        </div>
                      </TabsContent>
                    )}

                    {pickem && (
                      <TabsContent value="live-scoring" className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">Live Scoring</h3>
                            <p className="text-sm text-gray-600">
                              View and manage live scoring for all questions
                            </p>
                          </div>
                        </div>
                        <ContentCard>
                          <LiveScoringQuestionsList pickemId={pickem.pickem_id} />
                        </ContentCard>
                      </TabsContent>
                    )}

                    {pickem && (
                      <TabsContent value="entries" className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">Entries</h3>
                            <p className="text-sm text-gray-600">
                              {entriesData?.totalSubmitted ?? 0} users submitted picks &middot;{' '}
                              {entriesData?.totalReserved ?? 0} awaiting picks &middot;{' '}
                              {entriesData?.totalEverReserved ?? 0} reserved a seat at some point
                            </p>
                          </div>
                        </div>

                        {/* Search bar */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            type="text"
                            placeholder="Search by SSO ID or username..."
                            value={entriesSearchInput}
                            onChange={e => setEntriesSearchInput(e.target.value)}
                            className="pl-10"
                          />
                        </div>

                        {entriesLoading ? (
                          <div className="text-center py-8">Loading entries...</div>
                        ) : entriesData?.entries && entriesData.entries.length > 0 ? (
                          <>
                            <div className="border rounded-md overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8 dark:text-gray-400"></th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                      SSO User ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                      Entry Date
                                    </th>
                                    {isPickemComplete && (
                                      <>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                          Points
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                          Rank
                                        </th>
                                      </>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                  {entriesData.entries.map(entry => (
                                    <>
                                      <tr
                                        key={entry.userId}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                        onClick={() =>
                                          setExpandedEntryId(
                                            expandedEntryId === entry.userId ? null : entry.userId
                                          )
                                        }
                                      >
                                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                          {expandedEntryId === entry.userId ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">
                                          {entry.ssoUserId || entry.userId}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                          {entry.entryDate ? (
                                            <TzDateDisplay
                                              utcDate={entry.entryDate}
                                              formatString="MMM d, yyyy h:mm a"
                                            />
                                          ) : (
                                            'N/A'
                                          )}
                                        </td>
                                        {isPickemComplete && (
                                          <>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
                                              {entry.points !== null ? entry.points : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
                                              {entry.rank !== null ? `#${entry.rank}` : '-'}
                                            </td>
                                          </>
                                        )}
                                      </tr>
                                      {expandedEntryId === entry.userId && (
                                        <tr key={`${entry.userId}-details`}>
                                          <td colSpan={isPickemComplete ? 5 : 3} className="p-0">
                                            <SelectionsDetail
                                              pickemId={pickem.pickem_id}
                                              userId={entry.userId}
                                              username={entry.ssoUserId || entry.userId}
                                            />
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination controls */}
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-muted-foreground">
                                {(() => {
                                  const total = entriesData.totalEntries;
                                  const currentPage = entriesData.page;
                                  const limit = entriesData.limit;
                                  if (total === 0) return 'No entries';
                                  const start = (currentPage - 1) * limit + 1;
                                  const end = Math.min(currentPage * limit, total);
                                  return `Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()} entries`;
                                })()}
                              </p>
                              <div className="flex items-center gap-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEntriesPage(p => Math.max(1, p - 1))}
                                  disabled={entriesData.page <= 1}
                                >
                                  <ChevronLeft className="h-4 w-4 mr-1" />
                                  Previous
                                </Button>
                                <span className="text-sm text-muted-foreground">
                                  Page {entriesData.page.toLocaleString()} of{' '}
                                  {entriesData.totalPages.toLocaleString()}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setEntriesPage(p => Math.min(entriesData.totalPages, p + 1))
                                  }
                                  disabled={entriesData.page >= entriesData.totalPages}
                                >
                                  Next
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </div>
                            </div>
                          </>
                        ) : entriesDebouncedSearch ? (
                          <div className="text-center py-8 text-gray-500">
                            No entries match your search
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">No entries yet</div>
                        )}
                      </TabsContent>
                    )}

                    {isPickemComplete && (
                      <TabsContent value="leaderboard" className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">Top 500 Leaderboard</h3>
                            <p className="text-sm text-gray-600">
                              {leaderboardData?.selectionsCount || 0} total participants
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {!pickem?.scored && (
                              <Button
                                onClick={handleForceScoring}
                                disabled={startScoringIsPending}
                                className="gap-2"
                                type="button"
                              >
                                <Trophy className="w-4 h-4" />
                                {startScoringIsPending ? 'Scoring...' : 'Score Pickem Now'}
                              </Button>
                            )}
                            {isPickemComplete &&
                              (leaderboardData?.leaderboard?.length ?? 0) > 0 && (
                                <>
                                  <Button
                                    onClick={handleRecalculateRanks}
                                    disabled={recalculateRanksIsPending}
                                    variant="outline"
                                    className="gap-2"
                                    type="button"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    {recalculateRanksIsPending
                                      ? 'Recalculating...'
                                      : 'Recalculate Ranks'}
                                  </Button>
                                  {pickem?.scored && (
                                    <Button
                                      onClick={handleCalculatePrizes}
                                      disabled={
                                        recalculatePrizesIsPending || !pickem?.payout_template_id
                                      }
                                      variant={pickem?.payout_template_id ? 'default' : 'outline'}
                                      className="gap-2"
                                      type="button"
                                      title={
                                        !pickem?.payout_template_id
                                          ? 'No payout template assigned to this pickem'
                                          : 'Calculate prize payouts for winners'
                                      }
                                    >
                                      <DollarSign className="w-4 h-4" />
                                      {recalculatePrizesIsPending
                                        ? 'Calculating...'
                                        : 'Calculate Prizes'}
                                    </Button>
                                  )}
                                </>
                              )}
                            <Button
                              onClick={handleDownloadCSV}
                              variant="outline"
                              className="gap-2"
                              type="button"
                            >
                              <Download className="w-4 h-4" />
                              Export CSV
                            </Button>
                          </div>
                        </div>

                        {leaderboardLoading ? (
                          <div className="text-center py-8">Loading leaderboard...</div>
                        ) : leaderboardData?.leaderboard &&
                          leaderboardData.leaderboard.length > 0 ? (
                          <div className="border rounded-md overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Rank
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    SSO User ID
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    Points
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    TB Margin
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    Entry Time
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    Prize
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {leaderboardData.leaderboard.map((entry: any) => (
                                  <tr key={entry.user_id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {entry.rank}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-mono text-gray-900">
                                      {entry.user?.sso_user_id || entry.user_id}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                      {entry.points}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                      {entry.tiebreakerMargin != null ? (
                                        <span
                                          className={
                                            Number(entry.tiebreakerMargin) === 0
                                              ? 'text-green-600 font-medium'
                                              : ''
                                          }
                                        >
                                          ±{entry.tiebreakerMargin}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                      {entry.entryDate ? (
                                        <span className="text-xs">
                                          {new Date(entry.entryDate).toLocaleString()}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                      {entry.prize ? (
                                        <div className="flex flex-col items-end gap-0.5">
                                          {entry.prize.cash > 0 && (
                                            <span className="text-green-600 font-medium">
                                              ${Number(entry.prize.cash).toFixed(2)}
                                            </span>
                                          )}
                                          {entry.prize.crowns > 0 && (
                                            <span className="text-yellow-600 text-xs">
                                              {Number(entry.prize.crowns).toLocaleString()} crowns
                                            </span>
                                          )}
                                          {entry.prize.other && (
                                            <span className="text-blue-600 text-xs">
                                              {entry.prize.other}
                                            </span>
                                          )}
                                          {!entry.prize.cash &&
                                            !entry.prize.crowns &&
                                            !entry.prize.other &&
                                            '-'}
                                        </div>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No leaderboard data available
                          </div>
                        )}
                      </TabsContent>
                    )}

                    {pickem && (
                      <TabsContent value="styling" className="space-y-4">
                        <PickemStylingTab
                          pickemId={pickem.pickem_id}
                          isDefaultStyle={pickem.is_default_style ?? false}
                          gameType={pickem.game_type as GameType}
                          titleEn={pickem.title_en}
                          tenantId={pickem.tenant_id}
                          tenantCode={tenants.find(t => t.tenant_id === pickem.tenant_id)?.code}
                        />
                        {!isPickemEditable && (
                          <Button
                            type="button"
                            onClick={async () => {
                              try {
                                await updateStylingMutateAsync({
                                  id: pickem.pickem_id,
                                  title_en: values.title_en || pickem.title_en,
                                  title_fr: values.title_fr,
                                  tenant_id: values.tenant_id || pickem.tenant_id,
                                  start: values.start || pickem.start || new Date().toISOString(),
                                  allow_selection_start: values.allow_selection_start,
                                  questions_posted_at: values.questions_posted_at || null,
                                  reserve_a_seat_enabled: values.reserve_a_seat_enabled ?? false,
                                  pickem_status: values.pickem_status,
                                  sport: values.sport,
                                  prize_text_en: values.prize_text_en || '',
                                  prize_text_fr: values.prize_text_fr,
                                  style_config: values.style_config,
                                } as any);
                              } catch {
                                // error toast handled by mutation
                              }
                            }}
                            disabled={updateStylingIsPending}
                            className="w-full"
                          >
                            {updateStylingIsPending ? 'Saving...' : 'Save Styling'}
                          </Button>
                        )}
                      </TabsContent>
                    )}
                  </Tabs>

                  <Dialog
                    open={bannerLibraryOpen}
                    onOpenChange={open => {
                      setBannerLibraryOpen(open);
                      if (!open) {
                        setBannerLibrarySearch('');
                      }
                    }}
                  >
                    <DialogContent className="max-w-5xl p-0">
                      <DialogHeader className="px-6 pt-6">
                        <DialogTitle>Select Jackpot Banner from Image Library</DialogTitle>
                        <DialogDescription>
                          Choose an image from tenant image library for this Pick&apos;em jackpot
                          banner.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="px-6 pb-6 space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              value={bannerLibrarySearch}
                              onChange={e => setBannerLibrarySearch(e.target.value)}
                              placeholder="Search images by filename..."
                              className="pl-9"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => refetchTenantImageLibrary()}
                            disabled={isTenantImageLibraryFetching}
                          >
                            <RefreshCw
                              className={
                                isTenantImageLibraryFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
                              }
                            />
                            Refresh
                          </Button>
                        </div>

                        <div className="max-h-[58vh] overflow-y-auto border rounded-md p-3">
                          {isTenantImageLibraryLoading ? (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                              Loading image library...
                            </div>
                          ) : isTenantImageLibraryError ? (
                            <div className="py-10 text-center text-sm text-red-600">
                              Failed to load image library.
                            </div>
                          ) : filteredTenantImages.length === 0 ? (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                              {(tenantImageLibrary?.images?.length ?? 0) === 0
                                ? 'No images found in this tenant image library.'
                                : 'No images match your search.'}
                            </div>
                          ) : (
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {filteredTenantImages.map(image => {
                                const displayName = getImageDisplayName(image.name);
                                return (
                                  <button
                                    key={image.name}
                                    type="button"
                                    className="text-left border rounded-md p-2 hover:border-primary transition-colors"
                                    onClick={() => {
                                      setFieldValue('text_prize_image_en', image.url);
                                      setBannerLibraryOpen(false);
                                      setBannerLibrarySearch('');
                                    }}
                                  >
                                    <div className="h-28 w-full overflow-hidden rounded border bg-muted/20 mb-2">
                                      <img
                                        src={image.url}
                                        alt={displayName}
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                      />
                                    </div>
                                    <div
                                      className="text-xs font-medium truncate"
                                      title={displayName}
                                    >
                                      {displayName}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {formatFileSize(image.size)}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Warning for short pickem duration */}
                  {durationWarning && (
                    <Alert className="bg-yellow-50 border-yellow-200">
                      <AlertDescription className="text-yellow-800">
                        ⚠️ This pickem will only be available to users for {durationWarning.hours}{' '}
                        hours and {durationWarning.minutes} mins. Consider adjusting the start and
                        open times.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Info message for non-editable pickems */}
                  {!isPickemEditable && pickemEditMessage && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertDescription className="text-blue-800">
                        {pickemEditMessage}
                      </AlertDescription>
                    </Alert>
                  )}

                  {isPickemEditable && (
                    <Button
                      type="submit"
                      data-testid="pickem-submit-button"
                      disabled={isSubmitting || createIsPending || updateIsPending}
                      className="w-full"
                    >
                      {isSubmitting || createIsPending || updateIsPending
                        ? 'Saving...'
                        : questionOrder
                          ? 'Save Pickem & Question Order'
                          : 'Save Pickem'}
                    </Button>
                  )}
                </Form>
              );
            }}
          </Formik>
        </CardContent>

        {/* Duplicate Pickem Dialog */}
        {pickem && (
          <DuplicatePickemDialog
            open={duplicateDialogOpen}
            onOpenChange={setDuplicateDialogOpen}
            onConfirm={handleDuplicatePickem}
            tenants={tenants}
            isLoading={duplicateIsPending}
          />
        )}

        {/* Auto-Score Modal. Feed-gated: it posts to /grading/preview and /grading/execute,
            which the backend does not register without a feed — and auto-scoring reads stats
            this client has no source for. Its questions are settled by hand instead. */}
        {pickem && isFeedEnabled() && (
          <AutoScoreModal
            pickemId={pickem.pickem_id}
            open={autoScoreModalOpen}
            onOpenChange={setAutoScoreModalOpen}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: queryKeys.questions.all });
            }}
          />
        )}

        {/* Delete Question Confirmation Dialog */}
        <ConfirmDialog
          open={!!questionToDelete}
          onOpenChange={open => !open && setQuestionToDelete(null)}
          title="Delete Question"
          description={`Are you sure you want to delete "${questionToDelete?.question_text_en || 'this question'}"? This will also delete all options and user selections for this question. This action cannot be undone.`}
          confirmLabel={deleteQuestionIsPending ? 'Deleting...' : 'Delete'}
          cancelLabel="Cancel"
          onConfirm={handleDeleteQuestion}
          variant="destructive"
        />
      </Card>
    </PageContainer>
  );
}
